import { useSetToast } from "@app/components/Toast";
import { useS3FilesManagement } from "@app/hooks/createFileHook";
import { useNavigation } from "@app/lib/navigation";
import { adminPaths } from "@app/routing/adminPaths";
import { LocaleSelect } from "@app/shared/components/Locale/LocaleSelect";
import { type SupportedLocales } from "@app/shared/utils/locales";
import { useEntities } from "@app/store/entityStore";
import {
    type NewS3File,
    newS3FileToCreateFileInput,
} from "@app/usecases/FileUseCases";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Icon } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { Chip } from "@design-system/Inputs/Chip";
import DropZone from "@design-system/Inputs/DropZone";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { Box } from "@design-system/Layout/Box";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { Text } from "@design-system/Typography/Text";
import { type ReleasedFrameworkCategory } from "@generated/client/graphql";
import { type FC, useMemo, useState } from "react";
import {
    useCreateEvsQuestionnaire,
    useCreateFrameworkProject,
    useFrameworkQuestionTags,
    useReleasedFrameworkCategories,
} from "../../data";
import { useCreateFrameworkProjectModalTranslations } from "./i18n";

const CreateFrameworkProjectModal: FC<{
    categoryId?: string;
}> = ({ categoryId }) => {
    const [open, setOpen] = useState(false);
    const { t, keys } = useCreateFrameworkProjectModalTranslations();
    const { navigateFromHome } = useNavigation();

    const acceptedFileTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    const [frameworkId, setFrameworkId] = useState<string | undefined>(
        categoryId,
    );
    const [entityId, setEntityId] = useState<string | undefined>(undefined);
    const [year, setYear] = useState<number | undefined>(undefined);
    const [displayYear, setDisplayYear] = useState<number | undefined>(
        undefined,
    );
    const [locale, setLocale] = useState<string | undefined>(undefined);
    const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
    const [uploadedFile, setUploadedFile] = useState<NewS3File | undefined>(
        undefined,
    );

    const { releasedFrameworkCategories } = useReleasedFrameworkCategories();
    const { entities } = useEntities();
    const { tags } = useFrameworkQuestionTags(frameworkId, year, locale);
    const { createFrameworkProject, isPending } = useCreateFrameworkProject();
    const { mutate: createEvsQuestionnaire, isPending: isEvsPending } =
        useCreateEvsQuestionnaire();
    const { uploadFiletoS3 } = useS3FilesManagement();
    const { setToastSuccess } = useSetToast();

    const frameworkCategorySelected = useMemo<
        ReleasedFrameworkCategory | undefined
    >(() => {
        const category = releasedFrameworkCategories?.find(
            (f) => f.cmsGroupId === frameworkId,
        );
        // Dirty fix: add 2026 option for evs-questionnaire, backed by 2025 CMS data
        if (
            category &&
            frameworkId === "evs-questionnaire" &&
            category.versions.includes("2025") &&
            !category.versions.includes("2026")
        ) {
            return { ...category, versions: [...category.versions, "2026"] };
        }
        return category;
    }, [frameworkId, releasedFrameworkCategories]);

    const isDisabled =
        !frameworkId ||
        !year ||
        !entityId ||
        !locale ||
        isPending ||
        (!!tags?.length && selectedTagIds.length === 0) ||
        (frameworkId === "evs-questionnaire" && !uploadedFile);

    const handleCreateFrameworkProject = async () => {
        if (frameworkId && year && entityId && locale) {
            if (frameworkId === "evs-questionnaire" && uploadedFile) {
                createEvsQuestionnaire({
                    s3File: newS3FileToCreateFileInput(uploadedFile),
                    year: year,
                    locale,
                    entityId,
                });
                setToastSuccess(t(keys.ecovadis_success_message));
            }
            if (frameworkId !== "evs-questionnaire") {
                createFrameworkProject(
                    {
                        frameworkCmsGroupId: frameworkId,
                        entityId,
                        locale,
                        tagIds: selectedTagIds,
                        year,
                    },
                    {
                        onSuccess: (response) => {
                            const createdProjectId =
                                response.createFrameworkProject.data?.id;

                            if (createdProjectId) {
                                navigateFromHome(
                                    `${adminPaths.frameworks}/${frameworkId}/${createdProjectId}`,
                                );
                            }
                        },
                        onSettled: () => {
                            setOpen(false);
                        },
                    },
                );
            }
        }
    };

    const isSelected = (tagId: string) => {
        return selectedTagIds.includes(tagId);
    };
    const handleTagClickMaker = (tagId: string) => () => {
        if (isSelected(tagId)) {
            setSelectedTagIds((prev) => prev.filter((id) => id !== tagId));
        } else {
            setSelectedTagIds((prev) => [...prev, tagId]);
        }
    };

    const handleFrameworkCategorySelected = (value: string[]) => {
        setFrameworkId(value[0]);
        setYear(undefined);
        setDisplayYear(undefined);
        setLocale(undefined);
        setSelectedTagIds([]);
    };

    const handleFileDrop = async (file: File) => {
        const newS3File = await uploadFiletoS3(file);
        setUploadedFile(newS3File);
    };

    return (
        <Modal open={open} onOpenChange={setOpen}>
            <Modal.Trigger asChild>
                <Button variant="flatPrimary" size="sm">
                    <Button.Icon name="plus" />
                    {t(keys.open_button)}
                </Button>
            </Modal.Trigger>
            <Modal.Overlay centerContent className="py-5">
                <Modal.Content className="w-fit max-w-[880px]">
                    <Modal.Header>
                        <Modal.Title>{t(keys.title)}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="flex flex-col gap-6 items-start max-h-[500px] overflow-y-auto pb-5">
                        <FlexCol gap="2">
                            <FlexCol gap="1">
                                <Text variant="body-lg-bold">
                                    {t(keys.settings)}
                                </Text>
                                <Text variant="body" color="secondary">
                                    {t(keys.settings_description)}
                                </Text>
                            </FlexCol>
                            <FlexRow gap="4">
                                <FlexCol className="flex-1" gap="1">
                                    <Dropdown
                                        value={[frameworkId ?? ""]}
                                        onValueChange={
                                            handleFrameworkCategorySelected
                                        }
                                        selectType="single"
                                    >
                                        <Dropdown.Trigger
                                            asChild
                                            className="w-full"
                                        >
                                            <Button
                                                variant="outlined"
                                                className="justify-between h-9 shadow-none border-tertiary"
                                            >
                                                <FlexRow gap="2">
                                                    <Icon name="file" />
                                                    <Text variant="body-bold">
                                                        {frameworkCategorySelected
                                                            ? frameworkCategorySelected.name
                                                            : t(
                                                                  keys.input_framework,
                                                              )}
                                                    </Text>
                                                </FlexRow>
                                                <Icon name="chevronDown" />
                                            </Button>
                                        </Dropdown.Trigger>
                                        <Dropdown.Content align="start">
                                            {releasedFrameworkCategories?.map(
                                                (f) => (
                                                    <Dropdown.SelectableItem
                                                        value={f.cmsGroupId}
                                                        key={f.cmsGroupId}
                                                        className="w-full justify-between"
                                                    >
                                                        {f.name}
                                                        <Dropdown.SelectedIndicator />
                                                    </Dropdown.SelectableItem>
                                                ),
                                            )}
                                        </Dropdown.Content>
                                    </Dropdown>

                                    <Text variant="body" color="secondary">
                                        {t(keys.input_framework_description)}
                                    </Text>
                                </FlexCol>

                                <FlexCol className="flex-1" gap="1">
                                    <LocaleSelect
                                        value={locale as SupportedLocales}
                                        onChange={setLocale}
                                        options={
                                            (releasedFrameworkCategories?.find(
                                                (f) =>
                                                    f.cmsGroupId ===
                                                    frameworkId,
                                            )?.locales as SupportedLocales[]) ||
                                            []
                                        }
                                        placeholder={t(keys.input_locale)}
                                        emptyState={t(
                                            keys.input_locale_empty_state,
                                        )}
                                        triggerButtonProps={{
                                            className:
                                                "w-full justify-between h-9 shadow-none border-tertiary",
                                            variant: "outlined",
                                        }}
                                    />

                                    <Text variant="body" color="secondary">
                                        {t(keys.input_locale_description)}
                                    </Text>
                                </FlexCol>
                            </FlexRow>
                        </FlexCol>

                        <FlexRow gap="4" className="w-full">
                            <Dropdown
                                onValueChange={(value) => {
                                    const selected = parseInt(value[0]);
                                    setDisplayYear(selected);
                                    // Dirty fix: 2026 evs-questionnaire uses 2025 CMS data
                                    if (
                                        frameworkId === "evs-questionnaire" &&
                                        selected === 2026
                                    ) {
                                        setYear(2025);
                                    } else {
                                        setYear(selected);
                                    }
                                }}
                                selectType="single"
                            >
                                <Dropdown.Trigger asChild className="w-full">
                                    <Button
                                        variant="outlined"
                                        className="justify-between h-9 shadow-none border-tertiary"
                                    >
                                        <FlexRow gap="2">
                                            <Button.Icon name="calendar" />
                                            <Text variant="body-bold">
                                                {displayYear
                                                    ? displayYear
                                                    : t(keys.input_period)}
                                            </Text>
                                        </FlexRow>
                                        <Icon name="chevronDown" />
                                    </Button>
                                </Dropdown.Trigger>
                                <Dropdown.Content align="start">
                                    {frameworkCategorySelected?.versions.map(
                                        (version) => (
                                            <Dropdown.SelectableItem
                                                value={version}
                                                key={version}
                                                className="w-full justify-between"
                                            >
                                                {version}
                                                <Dropdown.SelectedIndicator />
                                            </Dropdown.SelectableItem>
                                        ),
                                    )}
                                </Dropdown.Content>
                            </Dropdown>

                            <Dropdown
                                value={[entityId ?? ""]}
                                onValueChange={(value) => setEntityId(value[0])}
                                selectType="single"
                            >
                                <Dropdown.Trigger asChild className="w-full">
                                    <Button
                                        variant="outlined"
                                        className="justify-between h-9 shadow-none border-tertiary"
                                    >
                                        <FlexRow gap="2">
                                            <Icon name="building" />
                                            <Text variant="body-bold">
                                                {entities?.find(
                                                    (e) => e.id === entityId,
                                                )?.name ?? t(keys.input_entity)}
                                            </Text>
                                        </FlexRow>
                                        <Icon name="chevronDown" />
                                    </Button>
                                </Dropdown.Trigger>
                                <Dropdown.Content align="start">
                                    {entities?.map((e) => (
                                        <Dropdown.SelectableItem
                                            value={e.id}
                                            key={e.id}
                                        >
                                            {e.name}
                                            <Dropdown.SelectedIndicator />
                                        </Dropdown.SelectableItem>
                                    ))}
                                </Dropdown.Content>
                            </Dropdown>
                        </FlexRow>
                        {frameworkId !== undefined && !!tags?.length && (
                            <FlexRow>
                                <FlexCol gap="2" w="full">
                                    <Text variant="body-lg-bold">
                                        {t(keys.tags_section_title)}
                                    </Text>
                                    <Text variant="body" color="secondary">
                                        {t(keys.tags_section_description)}
                                    </Text>

                                    <FlexRow wrap="wrap" gap="2" w="full">
                                        {tags?.map((t) => (
                                            <Chip
                                                key={t.id}
                                                active={isSelected(t.id)}
                                                onClick={handleTagClickMaker(
                                                    t.id,
                                                )}
                                            >
                                                <Chip.Value>
                                                    {t.name}
                                                </Chip.Value>
                                            </Chip>
                                        ))}
                                    </FlexRow>
                                </FlexCol>
                            </FlexRow>
                        )}
                        {frameworkId === "evs-questionnaire" && (
                            <FlexCol className="w-full h-full">
                                {uploadedFile ? (
                                    <FlexCol gap="2">
                                        <Text variant="body-bold">
                                            {t(keys.uploaded_file)}
                                        </Text>
                                        <FlexRow
                                            justifyContent="start"
                                            alignItems="center"
                                            gap="2"
                                        >
                                            <Text variant="body">
                                                {uploadedFile.name}
                                            </Text>
                                            <Button
                                                variant="plain"
                                                size="sm"
                                                onClick={() =>
                                                    setUploadedFile(undefined)
                                                }
                                            >
                                                <Button.Icon name="trash" />
                                            </Button>
                                        </FlexRow>
                                    </FlexCol>
                                ) : (
                                    <DropZone
                                        accept={acceptedFileTypes}
                                        buttonText="Upload"
                                        multiple={false}
                                        size="lg"
                                        className="w-full h-full"
                                        onChange={handleFileDrop}
                                        loading={isEvsPending}
                                    >
                                        <Text variant="body-bold">
                                            {t(keys.upload_evs_questionnaire)}
                                        </Text>
                                    </DropZone>
                                )}
                            </FlexCol>
                        )}
                    </Modal.Body>

                    <Modal.Footer>
                        <Modal.CancelCTA>
                            {t(keys.action_button_cancel)}
                        </Modal.CancelCTA>
                        <Tooltip>
                            <Tooltip.Trigger asChild>
                                <Box>
                                    <Modal.ConfirmCTA
                                        disabled={isDisabled}
                                        onClick={handleCreateFrameworkProject}
                                        loading={isPending || isEvsPending}
                                    >
                                        {t(keys.action_button_create)}
                                    </Modal.ConfirmCTA>
                                </Box>
                            </Tooltip.Trigger>
                            {isDisabled && (
                                <Tooltip.Content>
                                    {t(
                                        keys.action_button_create_disabled_tooltip,
                                    )}
                                </Tooltip.Content>
                            )}
                        </Tooltip>
                    </Modal.Footer>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};

export default CreateFrameworkProjectModal;
